/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, LogIn, User, Users, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const SESSION_KEY = "rescate_session";

export default function LoginPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [phone, setPhone] = useState(params.get("phone") || "");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState(null); // null = not yet logged in; [] / [...] after lookup

  // Auto-restore previous session
  useEffect(() => {
    document.title = "Mi cuenta · Operación Rescate";
    const skip = params.get("logout") === "1";
    if (skip) {
      localStorage.removeItem(SESSION_KEY);
      return;
    }
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (!raw) return;
      const s = JSON.parse(raw);
      if (s?.accounts?.length === 1) {
        nav(`/mi-cuenta/${s.accounts[0].id}`);
      } else if (s?.accounts?.length > 1) {
        setAccounts(s.accounts);
        setPhone(s.phone || "");
      }
    } catch { /* */ }
  }, []);

  const saveSession = (phoneRaw, accs) => {
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify({
        phone: phoneRaw,
        accounts: accs,
        saved_at: new Date().toISOString(),
      }));
    } catch { /* */ }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!phone.replace(/\D/g, "").length || pin.length !== 4) {
      toast.error("Llena tu teléfono y los 4 dígitos del PIN");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/registrations/lookup", { phone, pin });
      const accs = data.accounts || [{ id: data.id, full_name: data.full_name }];
      saveSession(phone, accs);
      if (accs.length === 1) {
        toast.success(`¡Bienvenido${accs[0].full_name ? ", " + accs[0].full_name.split(" ")[0] : ""}!`);
        nav(`/mi-cuenta/${accs[0].id}`);
      } else {
        setAccounts(accs);
        toast.success(`Encontramos ${accs.length} inscripciones`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No te pudimos encontrar");
    } finally {
      setLoading(false);
    }
  };

  const displayPhone = phone.replace(/\D/g, "").replace(/(\d{3})(\d{3})(\d{0,4}).*/, (_, a, b, c) => c ? `${a}-${b}-${c}` : b ? `${a}-${b}` : a);

  // ---- Account picker view ----
  if (accounts && accounts.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black text-white relative overflow-hidden flex items-center justify-center px-6 py-10">
        <div className="absolute -top-40 left-1/4 w-96 h-96 bg-yellow-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl pointer-events-none" />
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="relative w-full max-w-md bg-white rounded-3xl p-8 shadow-2xl"
          data-testid="account-picker"
        >
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
              <Users className="w-8 h-8 text-black" />
            </div>
          </div>
          <h1 className="font-heading uppercase text-2xl text-center text-black mb-1">¿Quién entra?</h1>
          <p className="text-sm text-gray-600 text-center mb-6">
            Tenemos {accounts.length} inscripciones vinculadas a este teléfono
          </p>
          <div className="space-y-2.5">
            {accounts.map((a) => (
              <button
                key={a.id}
                onClick={() => nav(`/mi-cuenta/${a.id}`)}
                data-testid={`account-${a.id}`}
                className="w-full bg-gradient-to-r from-yellow-50 to-orange-50 hover:from-yellow-100 hover:to-orange-100 border-2 border-yellow-200 hover:border-orange-400 rounded-2xl p-4 text-left transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex-shrink-0 flex items-center justify-center text-black font-black">
                    <User className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-bold text-black truncate">{a.full_name}</div>
                      {a.camper_number_display && (
                        <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-yellow-200 text-yellow-900">
                          {a.camper_number_display}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {a.age_category === "adult" ? "Adulto" : a.age_category === "child" ? "Niño" : "3-6 años"}
                      {typeof a.percentage_paid === "number" && ` · ${a.percentage_paid}% pagado`}
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-orange-500 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            ))}
          </div>
          <button
            onClick={() => {
              localStorage.removeItem(SESSION_KEY);
              setAccounts(null);
              setPin("");
            }}
            className="block mx-auto mt-6 text-xs text-gray-500 hover:text-gray-700 underline"
            data-testid="logout-btn"
          >
            Salir e iniciar con otro teléfono
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black text-white relative overflow-hidden flex items-center justify-center px-6">
      <div className="absolute -top-40 left-1/4 w-96 h-96 bg-yellow-500/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl pointer-events-none" />

      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md bg-white rounded-3xl p-8 shadow-2xl"
        data-testid="login-form"
      >
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
            <LogIn className="w-8 h-8 text-black" />
          </div>
        </div>
        <h1 className="font-heading uppercase text-3xl text-center text-black mb-2">Mi cuenta</h1>
        <p className="text-sm text-gray-600 text-center mb-6">
          Accede para ver tu progreso, pagar cuotas y editar tu inscripción.
        </p>

        <div className="space-y-4">
          <div>
            <Label htmlFor="login-phone">Tu teléfono</Label>
            <Input
              id="login-phone"
              data-testid="login-phone"
              type="tel"
              value={displayPhone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="614-555-1234"
              className="mt-1 h-12 text-lg !text-black bg-white placeholder:text-gray-400"
            />
          </div>
          <div>
            <Label htmlFor="login-pin">PIN (últimos 4 dígitos del teléfono)</Label>
            <Input
              id="login-pin"
              data-testid="login-pin"
              type="tel"
              maxLength={4}
              inputMode="numeric"
              pattern="[0-9]*"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="••••"
              className="mt-1 h-14 text-center text-3xl tracking-[0.8em] font-bold !text-black bg-white placeholder:text-gray-300"
              autoFocus
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="w-full mt-6 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold uppercase tracking-wider"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Entrar"}
        </Button>
        <p className="mt-3 text-xs text-gray-500 text-center">
          ¿Aún no estás inscrito? <a href="/" className="text-orange-500 font-bold">Regístrate aquí</a>
        </p>
      </motion.form>
    </div>
  );
}
